import { CSSProperties, FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteFooter } from "../../components/footer/SiteFooter";
import { MarketingHeader } from "./MarketingHeader";
import "./Marketing.scss";

const responseExample = "<b>响应（Response）</b>{<br>　\"code\": 200,<br>　\"info\": \"success\",<br>　\"data\": {<br>　　\"duration_seconds\": 5.163,<br>　　\"audio_url\": \"https://xxxxxx.mp3\",<br>　　\"request_id\": \"3c668040-4bb2-4350-9042-xxxxxxx\"<br>　}<br>}";

const requestExamples: Record<string, string> = {
  "curl": "<b>请求（Request）</b>curl -X POST \\<br>　'https://shanhaistudio.lycheeai.com.cn/openapi/open/voice/zeroshot/infer' \\<br>　-H 'api_key: your_api_key' \\<br>　-F 'speaker_id=your_speaker_id' \\<br>　-F 'text=欢迎使用TTS服务' \\<br>　-F 'speed=1.0' \\<br>　-F 'volume=1.0' \\<br>　-F 'sample_rate=44100'",
  "python": "<b>请求（Request）</b>import requests<br>import json<br><br>if __name__ == \"__main__\":<br>　api_key = \"your_api_key\"<br><br>　files = [<br>　　(\"speaker_id\", (None, \"your_speaker_id\")),<br>　]<br>　data = {<br>　　\"text\": \"欢迎使用TTS服务\",<br>　　\"speed\": \"1.0\",<br>　　\"volume\": \"1.0\",<br>　　\"sample_rate\": \"24000\",<br>　}<br>　headers = {\"api_key\": api_key}<br><br>　response = requests.post(<br>　　url=\"https://shanhaistudio.lycheeai.com.cn/openapi/open/voice/zeroshot/infer\",<br>　　files=files, data=data, headers=headers,<br>　　timeout=(60, 60),<br>　)<br>　print(response.status_code, response.text)",
  "java": "<b>请求（Request）</b>import okhttp3.*;<br>import java.util.concurrent.TimeUnit;<br><br>public class VoiceInferDemo {<br>　public static void main(String[] args) throws Exception {<br>　　String apiKey = \"your_api_key\";<br><br>　　OkHttpClient client = new OkHttpClient().newBuilder()<br>　　　.connectTimeout(60, TimeUnit.SECONDS)<br>　　　.readTimeout(60, TimeUnit.SECONDS)<br>　　　.writeTimeout(60, TimeUnit.SECONDS)<br>　　　.build();<br><br>　　RequestBody formBody = new MultipartBody.Builder()<br>　　　.setType(MultipartBody.FORM)<br>　　　.addFormDataPart(\"speaker_id\", \"your_speaker_id\")<br>　　　.addFormDataPart(\"text\", \"欢迎使用TTS服务\")<br>　　　.addFormDataPart(\"speed\", \"1.0\")<br>　　　.addFormDataPart(\"volume\", \"1.0\")<br>　　　.addFormDataPart(\"sample_rate\", \"44100\")<br>　　　.build();<br><br>　　Request request = new Request.Builder()<br>　　　.url(\"https://shanhaistudio.lycheeai.com.cn/openapi/open/voice/zeroshot/infer\")<br>　　　.post(formBody)<br>　　　.addHeader(\"api_key\", apiKey)<br>　　　.build();<br><br>　　Response response = client.newCall(request).execute();<br>　　System.out.println(response.body().string());<br>　}<br>}"
};

export const Marketing: FC = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 850);

  useEffect(() => {
    let debounceTimer: number | undefined;
    const handleResize = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        setIsMobile(window.innerWidth <= 850);
      }, 150);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.clearTimeout(debounceTimer);
    };
  }, []);

  useEffect(() => {
    document.title = "Lychee Voice - 媲美科技智能语音平台";
    const root = rootRef.current;
    if (!root) return;

    const navigation = root.querySelector<HTMLElement>(".nav");
    let navigationStartTop = 0;
    const measureNavigation = () => { navigationStartTop = navigation ? navigation.getBoundingClientRect().top + window.scrollY : 0; };
    const updateNavigationStyle = () => { const scrolled = window.scrollY > 8; navigation?.classList.toggle("is-scrolled", scrolled); root.classList.toggle("marketing-home-v2--nav-fixed", Boolean(navigation) && window.scrollY >= navigationStartTop); };
    const observer = new IntersectionObserver((entries) => { entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add("is-visible"); }); }, { threshold: 0.14 });
    const toast = root.querySelector<HTMLElement>(".agent-toast");
    let toastTimer: number | undefined;
    const showToast = () => { if (!toast) return; toast.classList.add("is-open"); window.clearTimeout(toastTimer); toastTimer = window.setTimeout(() => toast.classList.remove("is-open"), 2600); };
    const showRequestExample = (language: string) => {
      const requestColumn = root.querySelector<HTMLElement>(".code-columns > div:first-child");
      const responseColumn = root.querySelector<HTMLElement>(".code-columns > div:nth-child(2)");
      const codeTabs = root.querySelector<HTMLElement>(".codebox .tabs");
      if (requestColumn && requestExamples[language]) requestColumn.innerHTML = requestExamples[language];
      if (responseColumn) responseColumn.innerHTML = responseExample;
      codeTabs?.querySelectorAll<HTMLElement>("[data-code-lang]").forEach((tab) => { tab.classList.toggle("active", tab.dataset.codeLang === language); });
    };
    const carousel = root.querySelector<HTMLElement>(".homepage-hero-carousel");
    const heroSlides = carousel ? Array.from(carousel.querySelectorAll<HTMLElement>("[data-hero-slide]")) : [];
    const heroControls = carousel ? Array.from(carousel.querySelectorAll<HTMLButtonElement>("[data-hero-target]")) : [];
    let heroIndex = Math.max(0, heroSlides.findIndex((slide) => slide.classList.contains("is-active")));
    let heroTimer: number | undefined;
    const setHeroSlide = (next: number) => { if (!heroSlides.length) return; heroIndex = (next + heroSlides.length) % heroSlides.length; heroSlides.forEach((slide, index) => slide.classList.toggle("is-active", index === heroIndex)); heroControls.forEach((control, index) => { const active = index === heroIndex; control.classList.toggle("is-active", active); control.setAttribute("aria-selected", String(active)); }); };
    const startHeroTimer = () => { window.clearInterval(heroTimer); if (heroSlides.length > 1) heroTimer = window.setInterval(() => setHeroSlide(heroIndex + 1), 5000); };
    const pauseHeroTimer = () => window.clearInterval(heroTimer);
    heroControls.forEach((control) => { control.addEventListener("click", () => { pauseHeroTimer(); setHeroSlide(Number(control.dataset.heroTarget || 0)); if (!carousel?.matches(":hover")) startHeroTimer(); }); });
    carousel?.addEventListener("mouseenter", pauseHeroTimer); carousel?.addEventListener("mouseleave", startHeroTimer);
    const hubModeTabs = Array.from(root.querySelectorAll<HTMLElement>("[data-hub-mode]"));
    const hubModePanel = root.querySelector<HTMLElement>(".hub-mode-panel");
    const setHubMode = (tab: HTMLElement) => { if (!hubModePanel) return; hubModeTabs.forEach((item) => { const active = item === tab; item.classList.toggle("is-active", active); item.setAttribute("aria-selected", String(active)); }); const setText = (selector: string, value?: string) => { const node = hubModePanel.querySelector<HTMLElement>(selector); if (node && value) node.textContent = value; }; setText(".hub-mode-kicker", tab.dataset.kicker); setText("h4", tab.dataset.title); setText("p", tab.dataset.description); setText(".hub-mode-input", tab.dataset.input); const meta = hubModePanel.querySelectorAll<HTMLElement>(".hub-mode-meta span"); if (meta[0] && tab.dataset.metaOne) meta[0].textContent = tab.dataset.metaOne; if (meta[1] && tab.dataset.metaTwo) meta[1].textContent = tab.dataset.metaTwo; setText(".hub-mode-result b", tab.dataset.result); setText(".hub-mode-result small", tab.dataset.resultMeta); hubModePanel.classList.remove("is-switching"); void hubModePanel.offsetWidth; hubModePanel.classList.add("is-switching"); };
    hubModeTabs.forEach((tab) => tab.addEventListener("click", () => setHubMode(tab)));
    const toyCard = root.querySelector<HTMLElement>("#agentHeroCase");
    const toyItems = toyCard ? Array.from(toyCard.querySelectorAll<HTMLElement>("[data-toy-item]")) : [];
    const toyDots = toyCard ? Array.from(toyCard.querySelectorAll<HTMLElement>(".toy-inner-dots span")) : [];
    const toyThumbs = toyCard ? Array.from(toyCard.querySelectorAll<HTMLButtonElement>("[data-toy-thumb]")) : [];
    const toyTitle = toyCard?.querySelector<HTMLElement>(".toy-copy h2");
    const toySubtitle = toyCard?.querySelector<HTMLElement>("[data-toy-subtitle]");
    const toyDesc = toyCard?.querySelector<HTMLElement>("[data-toy-desc]");
    const toyTagTitles = toyCard ? Array.from(toyCard.querySelectorAll<HTMLElement>("[data-toy-tag]")) : [];
    const toyTagSubtitles = toyCard ? Array.from(toyCard.querySelectorAll<HTMLElement>(".toy-feature > span")) : [];
    const toyCases = [
      { subtitle: "\u66e6\u4ed4 \u00b7 \u4f01\u4e1aIP\u5b9a\u5236\u73a9\u5076", title: "\u66e6\u4ed4", desc: "\u4f01\u4e1a\u5409\u7965\u7269\u7684\u6570\u5b57\u5316\u8eab\uff0c<br>\u54c1\u724c\u6545\u4e8b\u7684\u4e13\u5c5e\u8bb2\u8ff0\u8005\u3002", tags: [{ title: "\u4f01\u4e1aIP\u5b9a\u5236", sub: "\u54c1\u724c\u6545\u4e8b" }, { title: "\u666e\u901a\u8bdd", sub: "\u4e13\u5c5e\u8bb2\u8ff0" }] },
      { subtitle: "\u7ca4\u60a6 \u00b7 \u5cad\u5357\u9192\u72ee\u6587\u5316IP\u73a9\u5076", title: "\u7ca4\u60a6", desc: "\u5cad\u5357\u9192\u72ee\u6587\u5316IP\uff0c<br>\u4e00\u53e3\u5730\u9053\u7ca4\u8bed\u4e0e\u4f60\u503e\u5048\u3002", tags: [{ title: "\u5e7f\u4e1cIP", sub: "\u5cad\u5357\u9192\u72ee" }, { title: "\u7ca4\u8bed\u5bf9\u8bdd", sub: "\u5730\u9053\u503e\u5048" }] },
      { subtitle: "Baby\u7cbe\u7075 \u00b7 \u539f\u521b\u513f\u7ae5\u966a\u4f34\u73a9\u5076", title: "Baby\u7cbe\u7075", desc: "\u8f6f\u840c\u5c0f\u7cbe\u7075\uff0c<br>\u5b69\u5b50\u7684\u5341\u4e07\u4e2a\u4e3a\u4ec0\u4e48\u3002", tags: [{ title: "\u539f\u521bIP", sub: "\u513f\u7ae5\u966a\u4f34" }, { title: "\u7761\u524d\u6545\u4e8b", sub: "\u5341\u4e07\u4e2a\u4e3a\u4ec0\u4e48" }] },
    ];
    let toyIndex = Math.max(0, toyItems.findIndex((item) => item.classList.contains("is-center")));
    const setNodeTextKeepIcon = (node: HTMLElement | undefined, text: string, iconSelector: string) => {
      if (!node) return;
      const icon = node.querySelector<HTMLElement>(iconSelector);
      node.textContent = text;
      if (icon) node.prepend(icon);
    };
    const setToyCase = (next: number) => {
      if (!toyItems.length) return;
      toyIndex = (next + toyItems.length) % toyItems.length;
      toyItems.forEach((item, index) => {
        const offset = (index - toyIndex + toyItems.length) % toyItems.length;
        item.classList.toggle("is-center", offset === 0);
        item.classList.toggle("is-right", offset === 1);
        item.classList.toggle("is-left", offset === toyItems.length - 1);
      });
      toyDots.forEach((dot, index) => dot.classList.toggle("is-active", index === toyIndex));
      toyThumbs.forEach((thumb, index) => thumb.classList.toggle("is-active", index === toyIndex));
      const current = toyCases[toyIndex];
      if (!current) return;
      if (toyTitle) toyTitle.textContent = current.title;
      if (toySubtitle) toySubtitle.textContent = current.subtitle;
      if (toyDesc) toyDesc.innerHTML = current.desc;
      toyTagTitles.forEach((node, index) => setNodeTextKeepIcon(node, current.tags[index]?.title || "", ".toy-icon"));
      toyTagSubtitles.forEach((node, index) => setNodeTextKeepIcon(node, current.tags[index]?.sub || "", ".toy-mini-icon"));
    };
    setToyCase(toyIndex);
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const toyNext = target?.closest<HTMLElement>("[data-toy-next]");
      if (toyNext) { event.preventDefault(); setToyCase(toyIndex + 1); return; }
      const toyThumb = target?.closest<HTMLElement>("[data-toy-thumb]");
      if (toyThumb?.dataset.toyThumb) { event.preventDefault(); setToyCase(Number(toyThumb.dataset.toyThumb)); return; }
      const play = target?.closest<HTMLButtonElement>(".play");
      if (play) { const playing = play.dataset.playing === "true"; play.dataset.playing = String(!playing); play.textContent = playing ? "▶" : "Ⅱ"; play.parentElement?.querySelectorAll<HTMLElement>(".audio-bars i").forEach((bar) => { bar.style.animationPlayState = playing ? "running" : "paused"; }); return; }
      const tab = target?.closest<HTMLElement>("[data-code-lang]");
      if (tab?.dataset.codeLang) { event.preventDefault(); showRequestExample(tab.dataset.codeLang); return; }
      const comingSoon = target?.closest<HTMLElement>("[data-coming-soon]");
      if (comingSoon) { event.preventDefault(); showToast(); return; }
      const anchor = target?.closest<HTMLAnchorElement>("a[href]");
      const href = anchor?.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("tel:")) return;
      if (anchor?.target === "_blank" || anchor?.hasAttribute("download") || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (href.startsWith("/")) { event.preventDefault(); navigate(href); }
    };
    measureNavigation(); window.addEventListener("scroll", updateNavigationStyle, { passive: true }); window.addEventListener("resize", measureNavigation); updateNavigationStyle(); startHeroTimer(); root.querySelectorAll(".reveal").forEach((node) => observer.observe(node)); root.addEventListener("click", handleClick); showRequestExample("python");
    return () => { window.removeEventListener("scroll", updateNavigationStyle); window.removeEventListener("resize", measureNavigation); window.clearInterval(heroTimer); window.clearTimeout(toastTimer); carousel?.removeEventListener("mouseenter", pauseHeroTimer); carousel?.removeEventListener("mouseleave", startHeroTimer); observer.disconnect(); root.removeEventListener("click", handleClick); };
  }, [navigate]);

  return (
    <div ref={rootRef} className={`marketing marketing-home-v2${isMobile ? " is-mobile" : ""}`}>
      <MarketingHeader />
      <main id={"top"}>
        <section className={"hero wrap homepage-hero-carousel"} aria-label={"Lychee Voice 产品首屏"}>
          <article className={"hero-slide is-active"} data-hero-slide={"0"}>
            <div className={"wave-bg"} aria-hidden={"true"}>
              <svg viewBox={"0 0 800 440"} preserveAspectRatio={"none"}>
                <path d={"M-20 145 C120 20 230 320 390 170 S630 20 840 170"}></path>
                <path d={"M-20 175 C120 45 230 350 390 200 S630 50 840 200"}></path>
                <path d={"M-20 205 C120 70 230 380 390 230 S630 80 840 230"}></path>
                <path d={"M-20 235 C120 95 230 410 390 260 S630 110 840 260"}></path>
                <path d={"M-20 265 C120 120 230 440 390 290 S630 140 840 290"}></path>
                <path d={"M-20 295 C120 145 230 470 390 320 S630 170 840 320"}></path>
                <path d={"M-20 325 C120 170 230 500 390 350 S630 200 840 350"}></path>
              </svg>
            </div>
            <div className={"hero-copy"}>
              <p className={"eyebrow"}>
                {"LYCHEE HUB · AI 创作平台"}
              </p>
              <h1>
                {"灵感有声，创作自然"}
              </h1>
              <p className={"lead"}>
                {"从文本转语音、AI 配音到播客制作，一站式的 AI 声音创作平台，让每一个想法都能即刻成为动听的有声内容。"}
              </p>
              <div className={"hero-actions"}>
                <a className={"btn black large"} href={"/hub/ai-voice"}>
                  {"探索 Lychee Hub"}
                </a>
              </div>
            </div>
            <div className={"hero-creation-card"} aria-label={"Lychee Hub 有声创作预览"}>
              <div className={"creation-status"}>
                <i>
                  {"♬"}
                </i>
                <b>
                  {"文本转语音"}
                </b>
                <em>
                  {"· 已准备生成"}
                </em>
              </div>
              <div className={"creation-editor"}>
                {"今天，让故事从声音开始。"}
                <span>
                  {"✧"}
                </span>
              </div>
              <div className={"creation-controls"}>
                <span className={"creation-select"}>
                  <i></i>
                  {"晚悠 · 女声　⌄"}
                </span>
                <span className={"creation-select model"}>
                  <i></i>
                  {"Voice TTS-Pro　⌄"}
                </span>
                {!isMobile && <button className={"creation-send"} type={"button"} aria-label={"生成语音"}>{"✧　→"}</button>}
              </div>
              <div className={"creation-player"}>
                <button className={"creation-play"} type={"button"} aria-label={"播放"}>
                  {"▶"}
                </button>
                <b className={"creation-time"}>
                  {"00:08"}
                </b>
                <div className={"hero-voice-wave audio-bars"} data-wave={"hub-hero-audio"}>
                  <i style={{"--h":"7%","--delay":"0s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"10%","--delay":"-0.14s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"8%","--delay":"-0.28s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"14%","--delay":"-0.42000000000000004s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"26%","--delay":"-0.56s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"11%","--delay":"-0.7000000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"19%","--delay":"-0.8400000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"8%","--delay":"-0.9800000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"12%","--delay":"-1.12s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"28%","--delay":"0s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"13%","--delay":"-0.14s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"18%","--delay":"-0.28s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"7%","--delay":"-0.42000000000000004s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"11%","--delay":"-0.56s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"23%","--delay":"-0.7000000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"14%","--delay":"-0.8400000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"10%","--delay":"-0.9800000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"18%","--delay":"-1.12s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"7%","--delay":"0s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"12%","--delay":"-0.14s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"31%","--delay":"-0.28s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"11%","--delay":"-0.42000000000000004s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"17%","--delay":"-0.56s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"9%","--delay":"-0.7000000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"13%","--delay":"-0.8400000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"24%","--delay":"-0.9800000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"43%","--delay":"-1.12s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"68%","--delay":"0s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"39%","--delay":"-0.14s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"17%","--delay":"-0.28s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"11%","--delay":"-0.42000000000000004s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"15%","--delay":"-0.56s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"29%","--delay":"-0.7000000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"18%","--delay":"-0.8400000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"10%","--delay":"-0.9800000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"14%","--delay":"-1.12s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"26%","--delay":"0s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"34%","--delay":"-0.14s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"16%","--delay":"-0.28s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"9%","--delay":"-0.42000000000000004s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"15%","--delay":"-0.56s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"23%","--delay":"-0.7000000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"11%","--delay":"-0.8400000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"8%","--delay":"-0.9800000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"17%","--delay":"-1.12s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"29%","--delay":"0s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"14%","--delay":"-0.14s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"10%","--delay":"-0.28s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"22%","--delay":"-0.42000000000000004s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"12%","--delay":"-0.56s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"7%","--delay":"-0.7000000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"14%","--delay":"-0.8400000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"10%","--delay":"-0.9800000000000001s","--speed":"1.8s"} as CSSProperties}></i>
                  <i style={{"--h":"8%","--delay":"-1.12s","--speed":"1.8s"} as CSSProperties}></i>
                </div>
              </div>
              {isMobile && <button className={"creation-send"} type={"button"} aria-label={"生成语音"}>{"✧　→"}</button>}
              <div className={"creation-usecases"}>
                <span>
                  <i className={"usecase-book"}></i>
                  {"故事旁白"}
                </span>
                <span>
                  <i className={"usecase-mic"}></i>
                  {"播客开场"}
                </span>
                <span>
                  <i className={"usecase-video"}></i>
                  {"视频配音"}
                </span>
              </div>
            </div>
          </article>
          <article className={"hero-slide"} data-hero-slide={"1"}>
            <div className={"wave-bg"} aria-hidden={"true"}>
              <svg viewBox={"0 0 800 440"} preserveAspectRatio={"none"}>
                <path d={"M-20 145 C120 20 230 320 390 170 S630 20 840 170"}></path>
                <path d={"M-20 175 C120 45 230 350 390 200 S630 50 840 200"}></path>
                <path d={"M-20 205 C120 70 230 380 390 230 S630 80 840 230"}></path>
                <path d={"M-20 235 C120 95 230 410 390 260 S630 110 840 260"}></path>
                <path d={"M-20 265 C120 120 230 440 390 290 S630 140 840 290"}></path>
                <path d={"M-20 295 C120 145 230 470 390 320 S630 170 840 320"}></path>
                <path d={"M-20 325 C120 170 230 500 390 350 S630 200 840 350"}></path>
              </svg>
            </div>
            <div className={"hero-copy"}>
              <p className={"eyebrow"}>
                {"LYCHEE AGENT · 语音智能体"}
              </p>
              <h1>
                {"实时交互，智慧相伴"}
              </h1>
              <p className={"lead"}>
                {"面向智能硬件，语音客服等场景，提供实时语音交互、多轮记忆与功能调用，打造如真人交谈般自然流畅的连续对话体验。"}
              </p>
              <div className={"hero-actions"}>
                <a className={"btn black large"} href={"/agent"}>
                  {"查看玩偶案例"}
                </a>
              </div>
            </div>
            <div className={"api-console toy-showcase"} id={"agentHeroCase"} aria-label={"曦仔玩偶展示"}>
              <span className={"toy-badge"}>
                <i></i>
                {"会说话的玩偶"}
              </span>
              <div className={"toy-copy"}>
                <h2>
                  {"曦仔"}
                </h2>
                <p className={"toy-case-subtitle"} data-toy-subtitle={""}>
                  {"曦仔 · 企业IP定制玩偶"}
                </p>
                <p data-toy-desc={""}>
                  {"企业吉祥物的数字化身，"}
                  <br />
                  {"品牌故事的专属讲述者。"}
                </p>
              </div>
              <div className={"toy-features"}>
                <div className={"toy-feature"}>
                  <strong data-toy-tag={"0"}>
                    <span className={"toy-icon"}>
                      <svg viewBox={"0 0 20 20"} aria-hidden={"true"}>
                        <path d={"M4 8v4M7 5v10M10 7v6M13 4v12M16 8v4"}></path>
                      </svg>
                    </span>
                    {"企业IP定制"}
                  </strong>
                  <span>
                    <span className={"toy-mini-icon"}>
                      <svg viewBox={"0 0 20 20"} aria-hidden={"true"}>
                        <path d={"M10 3l1.8 4.2L16 9l-4.2 1.8L10 15l-1.8-4.2L4 9l4.2-1.8L10 3Z"}></path>
                      </svg>
                    </span>
                    {"品牌故事"}
                  </span>
                </div>
                <div className={"toy-feature"}>
                  <strong data-toy-tag={"1"}>
                    <span className={"toy-icon"}>
                      <svg viewBox={"0 0 20 20"} aria-hidden={"true"}>
                        <path d={"M10 16s-6-3.5-6-8a3.2 3.2 0 0 1 5.7-2A3.2 3.2 0 0 1 16 8c0 4.5-6 8-6 8Z"}></path>
                      </svg>
                    </span>
                    {"普通话"}
                  </strong>
                  <span>
                    <span className={"toy-mini-icon"}>
                      <svg viewBox={"0 0 20 20"} aria-hidden={"true"}>
                        <path d={"M6 5h8a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7l-3 2V7a2 2 0 0 1 2-2Z"}></path>
                        <path d={"M8 9h4M8 12h3"}></path>
                      </svg>
                    </span>
                    {"专属讲述"}
                  </span>
                </div>
              </div>
              <div className={"toy-stage"} aria-hidden={"true"}>
                <img className={"toy-main is-center"} data-toy-item={"0"} src={"/img/lychee-agent/case-xizai.png"} alt={""} />
                <img className={"toy-main is-right"} data-toy-item={"1"} src={"/img/lychee-agent/case-yueyue.png"} alt={""} />
                <img className={"toy-main is-left"} data-toy-item={"2"} src={"/img/lychee-agent/case-baby.png"} alt={""} />
              </div>
              <button className={"toy-next"} type={"button"} aria-label={"Next toy case"} data-toy-next={""}>
                <svg viewBox={"0 0 20 20"} aria-hidden={"true"}>
                  <path d={"M8 5l5 5-5 5"}></path>
                </svg>
              </button>
              <div className={"toy-inner-dots"} aria-hidden={"true"}>
                <span className={"is-active"}></span>
                <span></span>
                <span></span>
              </div>
              <div className={"toy-thumbs"} aria-hidden={"true"}>
                <button type={"button"} className={"is-active"} data-toy-thumb={"0"}>
                  <img src={"/img/lychee-agent/case-xizai.png"} alt={""} />
                </button>
                <button type={"button"} data-toy-thumb={"1"}>
                  <img src={"/img/lychee-agent/case-yueyue.png"} alt={""} />
                </button>
                <button type={"button"} data-toy-thumb={"2"}>
                  <img src={"/img/lychee-agent/case-baby.png"} alt={""} />
                </button>
              </div>
            </div>

          </article>
          <article className={"hero-slide"} data-hero-slide={"2"}>
            <div className={"wave-bg"} aria-hidden={"true"}>
              <svg viewBox={"0 0 800 440"} preserveAspectRatio={"none"}>
                <path d={"M-20 145 C120 20 230 320 390 170 S630 20 840 170"}></path>
                <path d={"M-20 175 C120 45 230 350 390 200 S630 50 840 200"}></path>
                <path d={"M-20 205 C120 70 230 380 390 230 S630 80 840 230"}></path>
                <path d={"M-20 235 C120 95 230 410 390 260 S630 110 840 260"}></path>
                <path d={"M-20 265 C120 120 230 440 390 290 S630 140 840 290"}></path>
                <path d={"M-20 295 C120 145 230 470 390 320 S630 170 840 320"}></path>
                <path d={"M-20 325 C120 170 230 500 390 350 S630 200 840 350"}></path>
              </svg>
            </div>
            <div className={"hero-copy"}>
              <p className={"eyebrow"}>
                {"LYCHEE API · 开放能力"}
              </p>
              <h1>
                {"轻量集成，无限可能"}
              </h1>
              <p className={"lead"}>
                {"提供语音合成、识别、音色设计等标准接口，开发者可灵活调用，快速为产品注入富有情感与表现力的声音体验。"}
              </p>
              <div className={"hero-actions"}>
                <a className={"btn black large"} href={"/api_doc"}>
                  {"查看 API 文档"}
                </a>
              </div>
            </div>
            <div className={"api-console"}>
              <div className={"console-tabs"}>
                <span>
                  {"实时对话"}
                </span>
                <span>
                  {"语音生成"}
                </span>
                <span>
                  {"音色"}
                </span>
                <span className={"console-live"}>
                  {"LIVE"}
                </span>
              </div>
              <div className={"console-row"}>
                <span className={"console-avatar"}>
                  {"●"}
                </span>
                <div>
                  <b>
                    {"用户"}
                  </b>
                  <p>
                    {"帮我查查明天上海的天气怎么样？"}
                  </p>
                </div>
                <div className={"api-wave audio-bars"} data-wave={"hero"}>
                  <i style={{"--h":"51%","--delay":"0s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"70%","--delay":"-0.14s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"89%","--delay":"-0.28s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"45%","--delay":"-0.42000000000000004s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"64%","--delay":"-0.56s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"83%","--delay":"-0.7000000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"39%","--delay":"-0.8400000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"58%","--delay":"-0.9800000000000001s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"77%","--delay":"-1.12s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"33%","--delay":"0s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"52%","--delay":"-0.14s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"71%","--delay":"-0.28s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"90%","--delay":"-0.42000000000000004s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"46%","--delay":"-0.56s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"65%","--delay":"-0.7000000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"84%","--delay":"-0.8400000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"40%","--delay":"-0.9800000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"59%","--delay":"-1.12s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"78%","--delay":"0s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"34%","--delay":"-0.14s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"53%","--delay":"-0.28s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"72%","--delay":"-0.42000000000000004s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"28%","--delay":"-0.56s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"47%","--delay":"-0.7000000000000001s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"66%","--delay":"-0.8400000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"85%","--delay":"-0.9800000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"41%","--delay":"-1.12s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"60%","--delay":"0s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"79%","--delay":"-0.14s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"35%","--delay":"-0.28s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"54%","--delay":"-0.42000000000000004s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"73%","--delay":"-0.56s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"29%","--delay":"-0.7000000000000001s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"48%","--delay":"-0.8400000000000001s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"67%","--delay":"-0.9800000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"86%","--delay":"-1.12s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"42%","--delay":"0s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"61%","--delay":"-0.14s","--speed":"1.21s"} as CSSProperties}></i>
                </div>
              </div>
              <div className={"console-row"}>
                <span className={"console-avatar"}>
                  {"◌"}
                </span>
                <div>
                  <b>
                    {"Agent"}
                  </b>
                  <p>
                    {"明天上海多云，气温 18 到 24 度，适合出行。"}
                  </p>
                </div>
                <div className={"api-wave audio-bars"} data-wave={"console"}>
                  <i style={{"--h":"74%","--delay":"0s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"30%","--delay":"-0.14s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"49%","--delay":"-0.28s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"68%","--delay":"-0.42000000000000004s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"87%","--delay":"-0.56s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"43%","--delay":"-0.7000000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"62%","--delay":"-0.8400000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"81%","--delay":"-0.9800000000000001s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"37%","--delay":"-1.12s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"56%","--delay":"0s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"75%","--delay":"-0.14s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"31%","--delay":"-0.28s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"50%","--delay":"-0.42000000000000004s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"69%","--delay":"-0.56s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"88%","--delay":"-0.7000000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"44%","--delay":"-0.8400000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"63%","--delay":"-0.9800000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"82%","--delay":"-1.12s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"38%","--delay":"0s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"57%","--delay":"-0.14s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"76%","--delay":"-0.28s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"32%","--delay":"-0.42000000000000004s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"51%","--delay":"-0.56s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"70%","--delay":"-0.7000000000000001s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"89%","--delay":"-0.8400000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"45%","--delay":"-0.9800000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"64%","--delay":"-1.12s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"83%","--delay":"0s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"39%","--delay":"-0.14s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"58%","--delay":"-0.28s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"77%","--delay":"-0.42000000000000004s","--speed":"0.95s"} as CSSProperties}></i>
                </div>
              </div>
              <div className={"console-code-label"}>
                {"JavaScript　响应示例"}
              </div>
              <div className={"console-code"}>
                {"{"}
                <br />
                <span>
                  {"\"request_id\""}
                </span>
                {": \"req_8f3ac92d\","}
                <br />
                <span>
                  {"\"status\""}
                </span>
                {": \"success\","}
                <br />
                <span>
                  {"\"audio_url\""}
                </span>
                {": \"https://cdn.lycheevoice.com/...\","}
                <br />
                <span>
                  {"\"first_packet_ms\""}
                </span>
                {": 320"}
                <br />
                {"}"}
              </div>
            </div>
          </article>
          <div className={"homepage-hero-controls"} role={"tablist"} aria-label={"切换首页首屏"}>
            <button className={"homepage-hero-control is-active"} type={"button"} role={"tab"} aria-label={"切换至 Lychee Hub 首屏"} aria-selected={"true"} data-hero-target={"0"}></button>
            <button className={"homepage-hero-control"} type={"button"} role={"tab"} aria-label={"切换至 Lychee Agent 首屏"} aria-selected={"false"} data-hero-target={"1"}></button>
            <button className={"homepage-hero-control"} type={"button"} role={"tab"} aria-label={"切换至语音平台首屏"} aria-selected={"false"} data-hero-target={"2"}></button>
          </div>
        </section>
        <section className={"trust-strip"}>
          <div className={"wrap"}>
            <strong>
              {"为产品团队、开发者与Agent而建"}
            </strong>
            <span>
              <i>
                {"·"}
              </i>
              {" 实时语音交互"}
            </span>
            <span>
              <i>
                {"·"}
              </i>
              {" 可管理的声音资产"}
            </span>
            <span>
              <i>
                {"·"}
              </i>
              {" 开放 API 与调用示例"}
            </span>
          </div>
        </section>
{/*         
        <section className={"product-landing hub reveal"} id={"hub"}>
          <div className={"wrap product-landing-grid"}>
            <div className={"product-intro"}>
              <span className={"product-index"}>
                {"01"}
              </span>
              <h2>
                {"Lychee Hub"}
              </h2>
              <h3>
                {"从灵感到成片的声音工作台"}
              </h3>
              <p>
                {"把文案、主题、视频和声音灵感，快速变成可试听、可编辑、可发布的有声内容。"}
              </p>
              <div className={"product-actions"} aria-label={"Lychee Hub 能力"}>
                <span className={"product-chip"}>
                  {"AI 配音"}
                </span>
                <span className={"product-chip"}>
                  {"AI 播客"}
                </span>
                <span className={"product-chip"}>
                  {"视频译制"}
                </span>
                <span className={"product-chip"}>
                  {"声音设计"}
                </span>
              </div>
              <div className={"hub-intro-actions"}>
                <a className={"btn black"} href={"/hub/premium-translation"}>
                  {"进入 Lychee Hub　→"}
                </a>
                <a className={"btn"} href={"/hub/ai-voice"}>
                  {"查看创作案例"}
                </a>
              </div>
            </div>
            <div className={"hub-section-workbench hub-multimode-workbench"} aria-label={"Lychee Hub 多模式创作工作台示意"}>
              <div className={"hub-mode-header"}>
                <i className={"hub-mode-logo"}>
                  {"L"}
                </i>
                <b>
                  {"Lychee Hub"}
                </b>
                <span>
                  {"新建创作项目"}
                </span>
              </div>
              <div className={"hub-mode-tabs"} role={"tablist"} aria-label={"选择创作模式"}>
                <button className={"hub-mode-tab is-active"} type={"button"} role={"tab"} aria-selected={"true"} data-hub-mode={"tts"} data-kicker={"AI VOICEOVER"} data-title={"把文字变成自然的声音"} data-description={"选择音色和表达方式，快速生成可试听、可下载的配音。"} data-input={"输入一段文案，我们帮你自然地读出来"} data-meta-one={"晚悠 · 温柔女声"} data-meta-two={"Voice · TTS-Pro"} data-result={"品牌故事旁白"} data-result-meta={"已生成 · 00:24"}>
                  {"AI 配音"}
                </button>
                <button className={"hub-mode-tab"} type={"button"} role={"tab"} aria-selected={"false"} data-hub-mode={"podcast"} data-kicker={"AI PODCAST"} data-title={"把观点变成一场对话"} data-description={"从主题、文章或资料出发，自动生成有节奏的双人播客。"} data-input={"输入一个主题，或粘贴一篇你想聊的文章"} data-meta-one={"双人对话"} data-meta-two={"预计 8 分钟"} data-result={"《AI 改变生活》"} data-result-meta={"双人播客 · 已生成"}>
                  {"智能播客"}
                </button>
                <button className={"hub-mode-tab"} type={"button"} role={"tab"} aria-selected={"false"} data-hub-mode={"text"} data-kicker={"TEXT TO SPEECH"} data-title={"让文字自然地被听见"} data-description={"输入文案，选择声音与表达方式，即刻获得高质量语音。"} data-input={"输入想要转换为语音的文字内容"} data-meta-one={"默认音色 · 女声"} data-meta-two={"Voice · TTS-Pro"} data-result={"产品介绍旁白"} data-result-meta={"已生成 · 00:18"}>
                  {"文本转语音"}
                </button>
                <button className={"hub-mode-tab"} type={"button"} role={"tab"} aria-selected={"false"} data-hub-mode={"voice"} data-kicker={"VOICE DESIGN"} data-title={"为角色设计独一无二的声音"} data-description={"用自然语言描述声音性格，建立可持续使用的声音身份。"} data-input={"描述你想要的角色声音：沉稳、亲切、有一点故事感"} data-meta-one={"中文 · 男声"} data-meta-two={"情绪：温暖"} data-result={"Calm Storyteller"} data-result-meta={"新音色 · 已保存到音色库"}>
                  {"声音设计"}
                </button>
              </div>
              <div className={"hub-mode-panel"} aria-live={"polite"}>
                <span className={"hub-mode-kicker"}>
                  {"AI VOICEOVER"}
                </span>
                <h4>
                  {"把文字变成自然的声音"}
                </h4>
                <p>
                  {"选择音色和表达方式，快速生成可试听、可下载的配音。"}
                </p>
                <div className={"hub-mode-input"}>
                  {"输入一段文案，我们帮你自然地读出来"}
                </div>
                <div className={"hub-mode-meta"}>
                  <span>
                    {"晚悠 · 温柔女声"}
                  </span>
                  <span>
                    {"Voice · TTS-Pro"}
                  </span>
                  <button type={"button"} aria-label={"开始生成"}>
                    {"→"}
                  </button>
                </div>
                <div className={"hub-mode-result"}>
                  <i className={"hub-mode-play"}>
                    {"▶"}
                  </i>
                  <div>
                    <b>
                      {"品牌故事旁白"}
                    </b>
                    <small>
                      {"已生成 · 00:24"}
                    </small>
                  </div>
                  <div className={"hub-mode-wave"}>
                    <i style={{"--wave-height":"18%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"28%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"42%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"25%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"36%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"58%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"33%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"74%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"46%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"30%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"52%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"82%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"61%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"38%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"26%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"45%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"67%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"91%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"72%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"48%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"32%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"55%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"77%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"50%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"35%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"63%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"88%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"57%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"40%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"26%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"48%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"70%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"43%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"29%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"51%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"34%"} as CSSProperties}></i>
                    <i style={{"--wave-height":"22%"} as CSSProperties}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className={"product-landing agent reveal"} id={"agent-overview"}>
          <div className={"wrap product-landing-grid"}>
            <div className={"product-surface agent-surface"} aria-label={"Lychee Agent 对话示意"}>
              <aside className={"agent-rail"}>
                <span>
                  {"●"}
                </span>
                <span>
                  {"◉"}
                </span>
                <span>
                  {"◷"}
                </span>
                <span>
                  {"♙"}
                </span>
              </aside>
              <div className={"agent-chat"}>
                <div className={"agent-chat-head"}>
                  <b>
                    {"智能助手　"}
                    <span style={{"color":"#37ae75"} as CSSProperties}>
                      {"● 在线"}
                    </span>
                  </b>
                  <span>
                    {"⌕　×"}
                  </span>
                </div>
                <div className={"chat-bubble"}>
                  {"你好，我是 Lychee Agent，有什么可以帮你？"}
                </div>
                <div className={"chat-bubble agent-answer"}>
                  {"帮我创作一段适合睡前听的故事内容。"}
                </div>
                <div className={"chat-bubble"}>
                  {"为你推荐《故事 FM》，每一个真实故事，都值得用声音留下来。"}
                </div>
                <div className={"podcast-mini"}>
                  <i className={"podcast-cover"}>
                    {"故事"}
                    <br />
                    {"FM"}
                  </i>
                  <div>
                    <b>
                      {"故事 FM"}
                    </b>
                    <span>
                      {"用声音记录真实的故事与生活。"}
                    </span>
                  </div>
                  <span style={{"marginLeft":"auto","color":"#806ce8"} as CSSProperties}>
                    {"▶"}
                  </span>
                </div>
                <div className={"mic-button"}>
                  {"♬"}
                </div>
              </div>
            </div>
            <div className={"product-intro"}>
              <span className={"product-index"}>
                {"02"}
              </span>
              <h2>
                {"Lychee Agent"}
              </h2>
              <h3>
                {"让产品拥有自然的声音交互"}
              </h3>
              <p>
                {"低延迟、拟人化的语音对话体验，让用户与产品沟通更自然、更有温度。"}
              </p>
              <a className={"btn black"} href={"/hub/premium-translation"}>{"\u8054\u7cfb\u6211\u4eec"}</a>
              <div className={"agent-profile"}>
                <img className={"agent-avatar"} src={"/img/lychee-agent/xiaolu-avatar.png"} alt={"小鹿卡通头像"} />
                <div>
                  <b>
                    {"小鹿"}
                  </b>
                  <span>
                    {"自然、有温度的陪伴型声音"}
                  </span>
                </div>
                <svg className={"profile-wave"} viewBox={"0 0 152 28"} preserveAspectRatio={"none"} aria-label={"小鹿声音波形"}>
                  <rect x={"1"} y={"11"} width={"3"} height={"6"} rx={"1.5"}></rect>
                  <rect x={"7"} y={"8"} width={"3"} height={"12"} rx={"1.5"}></rect>
                  <rect x={"13"} y={"5"} width={"3"} height={"18"} rx={"1.5"}></rect>
                  <rect x={"19"} y={"9"} width={"3"} height={"10"} rx={"1.5"}></rect>
                  <rect x={"25"} y={"11"} width={"3"} height={"6"} rx={"1.5"}></rect>
                  <rect x={"31"} y={"7"} width={"3"} height={"14"} rx={"1.5"}></rect>
                  <rect x={"37"} y={"3"} width={"3"} height={"22"} rx={"1.5"}></rect>
                  <rect x={"43"} y={"6"} width={"3"} height={"16"} rx={"1.5"}></rect>
                  <rect x={"49"} y={"10"} width={"3"} height={"8"} rx={"1.5"}></rect>
                  <rect x={"55"} y={"12"} width={"3"} height={"4"} rx={"1.5"}></rect>
                  <rect x={"61"} y={"9"} width={"3"} height={"10"} rx={"1.5"}></rect>
                  <rect x={"67"} y={"4"} width={"3"} height={"20"} rx={"1.5"}></rect>
                  <rect x={"73"} y={"2"} width={"3"} height={"24"} rx={"1.5"}></rect>
                  <rect x={"79"} y={"6"} width={"3"} height={"16"} rx={"1.5"}></rect>
                  <rect x={"85"} y={"10"} width={"3"} height={"8"} rx={"1.5"}></rect>
                  <rect x={"91"} y={"12"} width={"3"} height={"4"} rx={"1.5"}></rect>
                  <rect x={"97"} y={"8"} width={"3"} height={"12"} rx={"1.5"}></rect>
                  <rect x={"103"} y={"5"} width={"3"} height={"18"} rx={"1.5"}></rect>
                  <rect x={"109"} y={"9"} width={"3"} height={"10"} rx={"1.5"}></rect>
                  <rect x={"115"} y={"11"} width={"3"} height={"6"} rx={"1.5"}></rect>
                  <rect x={"121"} y={"7"} width={"3"} height={"14"} rx={"1.5"}></rect>
                  <rect x={"127"} y={"4"} width={"3"} height={"20"} rx={"1.5"}></rect>
                  <rect x={"133"} y={"8"} width={"3"} height={"12"} rx={"1.5"}></rect>
                  <rect x={"139"} y={"10"} width={"3"} height={"8"} rx={"1.5"}></rect>
                  <rect x={"145"} y={"12"} width={"3"} height={"4"} rx={"1.5"}></rect>
                </svg>
              </div>
            </div>
          </div>
        </section>
        <section className={"product-landing api reveal"} id={"docs"}>
          <div className={"wrap product-landing-grid"}>
            <div className={"product-intro"}>
              <span className={"product-index"}>
                {"03"}
              </span>
              <h2>
                {"Lychee API"}
              </h2>
              <h3>
                {"为每一个产品接入声音智能"}
              </h3>
              <p>
                {"稳定高效的语音生成与交互能力，以简单易用的方式，快速集成到你的产品中。"}
              </p>
              <div className={"product-actions"} aria-label={"Lychee API 能力"}>
                <span className={"product-chip"}>
                  {"语音合成"}
                </span>
                <span className={"product-chip"}>
                  {"语音克隆"}
                </span>
                <span className={"product-chip"}>
                  {"音色设计"}
                </span>
                <span className={"product-chip"}>
                  {"实时语音"}
                </span>
              </div>
              <a className={"btn black"} href={"/api_doc"}>
                {"查看 API 文档　→"}
              </a>
            </div>
            <div className={"product-surface api-surface"} aria-label={"Lychee API 控制台示意"}>
              <div className={"api-tabs"}>
                <b>
                  {"API 控制台"}
                </b>
                <span>
                  {"文档"}
                </span>
                <span>
                  {"环境　正式环境　　API Key　••••••"}
                </span>
              </div>
              <div className={"api-body"}>
                <aside className={"api-menu"}>
                  <span>
                    {"⌂　概览"}
                  </span>
                  <span>
                    {"♬　语音合成"}
                  </span>
                  <span>
                    {"♙　语音识别"}
                  </span>
                  <span>
                    {"▤　声音理解"}
                  </span>
                  <span>
                    {"♧　音色管理"}
                  </span>
                  <span>
                    {"◷　实时语音"}
                  </span>
                </aside>
                <div className={"api-code"}>
                  <b>
                    {"请求示例"}
                  </b>
                  <pre>
                    {"curl -X POST \\\n  'https://shanhaistudio.lycheeai.com.cn/openapi/open/voice/zeroshot/infer' \\\n  -H 'api_key: YOUR_API_KEY' \\\n  -F 'speaker_id=your_speaker_id' \\\n  -F 'text=欢迎使用语音合成服务' \\\n  -F 'speed=1.0' \\\n  -F 'sample_rate=44100'"}
                  </pre>
                  <div className={"api-audio"}>
                    <i className={"api-play"}>
                      {"▶"}
                    </i>
                    <i className={"api-response-wave"}></i>
                    <span style={{"fontSize":"9px","color":"#777"} as CSSProperties}>
                      {"00:00 / 00:04"}
                    </span>
                  </div>
                </div>
                <aside className={"api-params"}>
                  <b>
                    {"参数"}
                  </b>
                  <span>
                    {"text　string　是"}
                  </span>
                  <span>
                    {"speaker_id　string　是"}
                  </span>
                  <span>
                    {"speed　number　否"}
                  </span>
                  <span>
                    {"sample_rate　string　否"}
                  </span>
                </aside>
              </div>
            </div>
          </div>
        </section>
*/}        
        
        <section className={"landing-cta wrap reveal"} id={"start"}>
          <div className={"landing-cta-inner"}>
            <svg className={"landing-wave"} viewBox={"0 0 1200 168"} preserveAspectRatio={"none"} aria-hidden={"true"}>
              <defs>
                <linearGradient id={"landing-wave-gradient"} x1={"0"} y1={"0"} x2={"1"} y2={"0"}>
                  <stop offset={"0%"} stop-color={"#6675d7"}></stop>
                  <stop offset={"34%"} stop-color={"#67dec9"}></stop>
                  <stop offset={"53%"} stop-color={"#d9e8f4"}></stop>
                  <stop offset={"69%"} stop-color={"#f1b171"}></stop>
                  <stop offset={"100%"} stop-color={"#d76d48"}></stop>
                </linearGradient>
                <filter id={"landing-wave-glow"} x={"-15%"} y={"-100%"} width={"130%"} height={"300%"}>
                  <feGaussianBlur stdDeviation={"5"} result={"blur"}></feGaussianBlur>
                  <feMerge>
                    <feMergeNode in={"blur"}></feMergeNode>
                    <feMergeNode in={"SourceGraphic"}></feMergeNode>
                  </feMerge>
                </filter>
              </defs>
              <path className={"wave-path wave-glow"} d={"M-20 84 C42 84 52 60 112 84 S177 108 242 84 S307 41 370 84 S436 120 502 84 S568 50 634 84 S700 110 766 84 S832 46 898 84 S964 108 1030 84 S1094 62 1220 84"}></path>
              <path className={"wave-path wave-main"} d={"M-20 84 C42 84 52 60 112 84 S177 108 242 84 S307 41 370 84 S436 120 502 84 S568 50 634 84 S700 110 766 84 S832 46 898 84 S964 108 1030 84 S1094 62 1220 84"}></path>
              <path className={"wave-path wave-fine"} d={"M-20 84 C42 84 54 68 112 84 S177 100 242 84 S307 57 370 84 S436 106 502 84 S568 63 634 84 S700 102 766 84 S832 58 898 84 S964 100 1030 84 S1094 70 1220 84"}></path>
              <circle className={"wave-particle"} cx={"81"} cy={"46"} r={"3"}></circle>
              <circle className={"wave-particle"} cx={"111"} cy={"120"} r={"2"}></circle>
              <circle className={"wave-particle"} cx={"1000"} cy={"45"} r={"3"}></circle>
              <circle className={"wave-particle warm"} cx={"1055"} cy={"120"} r={"3"}></circle>
              <circle className={"wave-particle warm"} cx={"1120"} cy={"48"} r={"2"}></circle>
            </svg>
            <div className={"landing-cta-copy"}>
              <h2>
                {"让声音，拓展创作边界，重塑交互体验"}
              </h2>
            </div>
          </div>
        </section>
        <section className={"capabilities wrap reveal"}>
          <h2 className={"section-title"}>
            {"完整的语音能力，为产品而生"}
          </h2>
          <div className={"cap-grid"}>
            <article className={"cap-card"}>
              <div className={"line-icon"}>
                <svg viewBox={"0 0 56 56"}>
                  <path d={"M14 6h19l10 10v31a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V10a4 4 0 0 1 4-4Z"}></path>
                  <path d={"M32 6v12h11M18 28h13M18 35h10"}></path>
                  <circle cx={"39"} cy={"39"} r={"10"}></circle>
                  <path d={"M39 34v10M35 37v4M43 37v4"}></path>
                </svg>
              </div>
              <h3>
                {"语音生成 API"}
              </h3>
              <p>
                {"高自然度、可控的语音输出，支持多语言、音色选择与语速调节。"}
              </p>
              <a href={"#docs"} className={"more"}>
                {"了解更多　→"}
              </a>
              <div className={"cap-note"}>
                {"自然流畅，精细可控"}
              </div>
            </article>
            <article className={"cap-card"}>
              <div className={"line-icon"}>
                <svg viewBox={"0 0 56 56"}>
                  <path d={"M16 20v-6a6 6 0 0 1 6-6h6M34 8h6a6 6 0 0 1 6 6v6M46 36v6a6 6 0 0 1-6 6h-6M22 48h-6a6 6 0 0 1-6-6v-6"}></path>
                  <circle cx={"28"} cy={"28"} r={"12"}></circle>
                  <path d={"M28 19v18M23 23v10M33 23v10"}></path>
                </svg>
              </div>
              <h3>
                {"音色智能 API"}
              </h3>
              <p>
                {"音色克隆与设计，打造专属声音身份，支持公共音色与自定义音色管理。"}
              </p>
              <a href={"#identity"} className={"more"}>
                {"了解更多　→"}
              </a>
              <div className={"cap-note"}>
                {"设计生成，灵活管理"}
              </div>
            </article>
            <article className={"cap-card featured-cap"}>
              <div className={"line-icon"}>
                <svg viewBox={"0 0 56 56"}>
                  <path d={"M11 11h34a6 6 0 0 1 6 6v21a6 6 0 0 1-6 6H27l-10 7v-7h-6a6 6 0 0 1-6-6V17a6 6 0 0 1 6-6Z"}></path>
                  <rect x={"18"} y={"21"} width={"20"} height={"14"} rx={"6"}></rect>
                  <circle cx={"24"} cy={"28"} r={"1"} fill={"currentColor"}></circle>
                  <circle cx={"32"} cy={"28"} r={"1"} fill={"currentColor"}></circle>
                </svg>
              </div>
              <h3>
                {"实时语音 Agent"}
              </h3>
              <p>
                {"低延迟、多轮对话的语音交互能力，打断、记忆与工具调用一体化。"}
              </p>
              <a href={"#agent"} className={"more"}>
                {"了解更多　→"}
              </a>
              <div className={"cap-note"}>
                {"打断响应，记忆与工具调用"}
              </div>
            </article>
          </div>
        </section>
        <section className={"code-section reveal"} id={"docs"}>
          <div className={"wrap split"}>
            <div>
              <h2>
                {"几行代码，"}
                <br />
                {"接入你的第一段语音体验"}
              </h2>
              <div className={"codebox"}>
                <div className={"tabs"}>
                  <span data-code-lang={"curl"}>
                    {"cURL"}
                  </span>
                  <span data-code-lang={"python"} className={"active"}>
                    {"Python"}
                  </span>
                  <span data-code-lang={"java"}>
                    {"Java"}
                  </span>
                  <span style={{"marginLeft":"auto"} as CSSProperties}>
                    {"⧉ 复制代码"}
                  </span>
                </div>
                <div className={"code-columns"}>
                  <div>
                    <b>
                      {"请求（Request）"}
                    </b>
                    {"import requests"}
                    <br />
                    {"import json"}
                    <br />
                    <br />
                    {"if __name__ == \"__main__\":"}
                    <br />
                    {"　api_key = \"your_api_key\""}
                    <br />
                    <br />
                    {"　files = ["}
                    <br />
                    {"　　(\"speaker_id\", (None, \"your_speaker_id\")),"}
                    <br />
                    {"　]"}
                    <br />
                    {"　data = {"}
                    <br />
                    {"　　\"text\": \"欢迎使用TTS服务\","}
                    <br />
                    {"　　\"speed\": \"1.0\","}
                    <br />
                    {"　　\"volume\": \"1.0\","}
                    <br />
                    {"　　\"sample_rate\": \"24000\","}
                    <br />
                    {"　}"}
                    <br />
                    {"　headers = {\"api_key\": api_key}"}
                    <br />
                    <br />
                    {"　response = requests.post("}
                    <br />
                    {"　　url=\"https://shanhaistudio.lycheeai.com.cn/openapi/open/voice/zeroshot/infer\","}
                    <br />
                    {"　　files=files, data=data, headers=headers,"}
                    <br />
                    {"　　timeout=(60, 60),"}
                    <br />
                    {"　)"}
                    <br />
                    {"　print(response.status_code, response.text)"}
                  </div>
                  <div>
                    <b>
                      {"响应（Response）"}
                    </b>
                    {"{"}
                    <br />
                    {"　\"code\": 200,"}
                    <br />
                    {"　\"info\": \"success\","}
                    <br />
                    {"　\"data\": {"}
                    <br />
                    {"　　\"duration_seconds\": 5.163,"}
                    <br />
                    {"　　\"audio_url\": \"https://xxxxxx.mp3\","}
                    <br />
                    {"　　\"request_id\": \"3c668040-4bb2-4350-9042-xxxxxxx\""}
                    <br />
                    {"　}"}
                    <br />
                    {"}"}
                  </div>
                </div>
                <div className={"code-cta"}>
                  <span>
                    {"5 分钟完成第一次语音调用"}
                  </span>
                  <a className={"btn"} href={"/api_doc"}>
                    {"查看 API 文档　→"}
                  </a>
                </div>
              </div>
            </div>
            <aside className={"metric-box"}>
              <h4>
                {"稳定可靠的工程化能力"}
              </h4>
              <div className={"metric"}>
                <i className={"metric-icon"}>
                  {"♢"}
                </i>
                <div>
                  <b>
                    {"99.9%"}
                  </b>
                  <span>
                    {"可用性"}
                  </span>
                </div>
              </div>
              <div className={"metric"}>
                <i className={"metric-icon"}>
                  {"◴"}
                </i>
                <div>
                  <b>
                    {"320ms"}
                  </b>
                  <span>
                    {"首包延迟"}
                  </span>
                </div>
              </div>
              <div className={"metric"}>
                <i className={"metric-icon"}>
                  {"◎"}
                </i>
                <div>
                  <b>
                    {"16 种语言"}
                  </b>
                  <span>
                    {"全球覆盖"}
                  </span>
                </div>
              </div>
              <a className={"btn"} href={"#"}>
                {"阅读文档　→"}
              </a>
            </aside>
          </div>
        </section>
        <div className={"section-divider"}></div>
        <section className={"product-section wrap reveal"} id={"agent"}>
          <div className={"product-split"}>
            <div className={"demo-panel"}>
              <div className={"chat-row"}>
                <div className={"chat-head"}>
                  <span>
                    {"用户（你）"}
                  </span>
                  <span>
                    {"00:04"}
                  </span>
                </div>
                <div className={"small-wave audio-bars"} data-wave={"user"}>
                  <i style={{"--h":"34%","--delay":"0s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"53%","--delay":"-0.14s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"72%","--delay":"-0.28s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"28%","--delay":"-0.42000000000000004s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"47%","--delay":"-0.56s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"66%","--delay":"-0.7000000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"85%","--delay":"-0.8400000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"41%","--delay":"-0.9800000000000001s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"60%","--delay":"-1.12s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"79%","--delay":"0s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"35%","--delay":"-0.14s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"54%","--delay":"-0.28s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"73%","--delay":"-0.42000000000000004s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"29%","--delay":"-0.56s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"48%","--delay":"-0.7000000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"67%","--delay":"-0.8400000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"86%","--delay":"-0.9800000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"42%","--delay":"-1.12s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"61%","--delay":"0s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"80%","--delay":"-0.14s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"36%","--delay":"-0.28s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"55%","--delay":"-0.42000000000000004s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"74%","--delay":"-0.56s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"30%","--delay":"-0.7000000000000001s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"49%","--delay":"-0.8400000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"68%","--delay":"-0.9800000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"87%","--delay":"-1.12s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"43%","--delay":"0s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"62%","--delay":"-0.14s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"81%","--delay":"-0.28s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"37%","--delay":"-0.42000000000000004s","--speed":"0.95s"} as CSSProperties}></i>
                </div>
              </div>
              <div className={"chat-row"}>
                <div className={"chat-head"}>
                  <span>
                    {"⚡ 打断"}
                  </span>
                  <span>
                    {"00:02"}
                  </span>
                </div>
              </div>
              <div className={"chat-row"}>
                <div className={"chat-head"}>
                  <span>
                    {"Agent（Lychee）"}
                  </span>
                  <span>
                    {"00:03"}
                  </span>
                </div>
                <div className={"small-wave audio-bars"} data-wave={"agent"}>
                  <i style={{"--h":"57%","--delay":"0s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"76%","--delay":"-0.14s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"32%","--delay":"-0.28s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"51%","--delay":"-0.42000000000000004s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"70%","--delay":"-0.56s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"89%","--delay":"-0.7000000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"45%","--delay":"-0.8400000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"64%","--delay":"-0.9800000000000001s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"83%","--delay":"-1.12s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"39%","--delay":"0s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"58%","--delay":"-0.14s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"77%","--delay":"-0.28s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"33%","--delay":"-0.42000000000000004s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"52%","--delay":"-0.56s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"71%","--delay":"-0.7000000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"90%","--delay":"-0.8400000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"46%","--delay":"-0.9800000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"65%","--delay":"-1.12s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"84%","--delay":"0s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"40%","--delay":"-0.14s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"59%","--delay":"-0.28s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"78%","--delay":"-0.42000000000000004s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"34%","--delay":"-0.56s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"53%","--delay":"-0.7000000000000001s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"72%","--delay":"-0.8400000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"28%","--delay":"-0.9800000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                  <i style={{"--h":"47%","--delay":"-1.12s","--speed":"1.08s"} as CSSProperties}></i>
                  <i style={{"--h":"66%","--delay":"0s","--speed":"1.21s"} as CSSProperties}></i>
                  <i style={{"--h":"85%","--delay":"-0.14s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                  <i style={{"--h":"41%","--delay":"-0.28s","--speed":"1.47s"} as CSSProperties}></i>
                  <i style={{"--h":"60%","--delay":"-0.42000000000000004s","--speed":"0.95s"} as CSSProperties}></i>
                </div>
              </div>
              <div className={"memory"}>
                <span>
                  {"对话记忆"}
                </span>
                <span>
                  {"用户偏好：喜欢故事和探索"}
                </span>
                <span>
                  {"音色：中性"}
                </span>
                <span>
                  {"上次话题：太空探索"}
                </span>
              </div>
            </div>
            <div className={"product-copy"}>
              <p className={"eyebrow"}>
                {"REAL-TIME VOICE AGENT"}
              </p>
              <h2>
                {"让产品真正听懂、"}
                <br />
                {"回应并持续对话"}
              </h2>
              <div className={"agent-benefits"}>
                <span>
                  {"实时语音识别与合成，低延迟自然对话"}
                </span>
                <span>
                  {"支持打断、快速响应用户意图"}
                </span>
                <span>
                  {"多轮记忆与上下文理解"}
                </span>
                <span>
                  {"工具调用与业务系统集成"}
                </span>
              </div>
              <a className={"btn black"} href={"#"}>
                {"了解实时语音方案　→"}
              </a>
            </div>
          </div>
        </section>
        <section className={"product-section soft reveal"}>
          <div className={"wrap product-split reverse"}>
            <div className={"voice-editor"}>
              <div className={"field-label"} style={{"marginTop":"0"} as CSSProperties}>
                {"语音生成"}
              </div>
              <div className={"textarea"}>
                {"在清晨的宇宙中，每一颗星星都在诉说着自己的故事。"}
                <span style={{"float":"right","color":"#aaa"} as CSSProperties}>
                  {"23/5000"}
                </span>
              </div>
              <div className={"select"}>
                <span className={"sound-chip"}>
                  {"⌁"}
                </span>
                {"Warm Narrator "}
                <span style={{"marginLeft":"auto"} as CSSProperties}>
                  {"温暖　中文　男性　⌄"}
                </span>
              </div>
              <div className={"mini-controls speed-only"}>
                <div>
                  {"语速"}
                  <div className={"mini-range"}></div>
                </div>
              </div>
              <div className={"voice-wave audio-bars"} data-wave={"tts"}>
                <i style={{"--h":"80%","--delay":"0s","--speed":"0.95s"} as CSSProperties}></i>
                <i style={{"--h":"36%","--delay":"-0.14s","--speed":"1.08s"} as CSSProperties}></i>
                <i style={{"--h":"55%","--delay":"-0.28s","--speed":"1.21s"} as CSSProperties}></i>
                <i style={{"--h":"74%","--delay":"-0.42000000000000004s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                <i style={{"--h":"30%","--delay":"-0.56s","--speed":"1.47s"} as CSSProperties}></i>
                <i style={{"--h":"49%","--delay":"-0.7000000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                <i style={{"--h":"68%","--delay":"-0.8400000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                <i style={{"--h":"87%","--delay":"-0.9800000000000001s","--speed":"1.21s"} as CSSProperties}></i>
                <i style={{"--h":"43%","--delay":"-1.12s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                <i style={{"--h":"62%","--delay":"0s","--speed":"1.47s"} as CSSProperties}></i>
                <i style={{"--h":"81%","--delay":"-0.14s","--speed":"0.95s"} as CSSProperties}></i>
                <i style={{"--h":"37%","--delay":"-0.28s","--speed":"1.08s"} as CSSProperties}></i>
                <i style={{"--h":"56%","--delay":"-0.42000000000000004s","--speed":"1.21s"} as CSSProperties}></i>
                <i style={{"--h":"75%","--delay":"-0.56s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                <i style={{"--h":"31%","--delay":"-0.7000000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                <i style={{"--h":"50%","--delay":"-0.8400000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                <i style={{"--h":"69%","--delay":"-0.9800000000000001s","--speed":"1.08s"} as CSSProperties}></i>
                <i style={{"--h":"88%","--delay":"-1.12s","--speed":"1.21s"} as CSSProperties}></i>
                <i style={{"--h":"44%","--delay":"0s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                <i style={{"--h":"63%","--delay":"-0.14s","--speed":"1.47s"} as CSSProperties}></i>
                <i style={{"--h":"82%","--delay":"-0.28s","--speed":"0.95s"} as CSSProperties}></i>
                <i style={{"--h":"38%","--delay":"-0.42000000000000004s","--speed":"1.08s"} as CSSProperties}></i>
                <i style={{"--h":"57%","--delay":"-0.56s","--speed":"1.21s"} as CSSProperties}></i>
                <i style={{"--h":"76%","--delay":"-0.7000000000000001s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                <i style={{"--h":"32%","--delay":"-0.8400000000000001s","--speed":"1.47s"} as CSSProperties}></i>
                <i style={{"--h":"51%","--delay":"-0.9800000000000001s","--speed":"0.95s"} as CSSProperties}></i>
                <i style={{"--h":"70%","--delay":"-1.12s","--speed":"1.08s"} as CSSProperties}></i>
                <i style={{"--h":"89%","--delay":"0s","--speed":"1.21s"} as CSSProperties}></i>
                <i style={{"--h":"45%","--delay":"-0.14s","--speed":"1.3399999999999999s"} as CSSProperties}></i>
                <i style={{"--h":"64%","--delay":"-0.28s","--speed":"1.47s"} as CSSProperties}></i>
                <i style={{"--h":"83%","--delay":"-0.42000000000000004s","--speed":"0.95s"} as CSSProperties}></i>
              </div>
            </div>
            <div className={"product-copy"}>
              <p className={"eyebrow"}>
                {"VOICE GENERATION"}
              </p>
              <h2>
                {"让每一句话，"}
                <br />
                {"都自然流畅地表达"}
              </h2>
              <p>
                {"支持文本转语音、音色选择与语速调节；"}
                <br />
                {"音色设计支持音高、情感与风格调节。"}
              </p>
              <a className={"btn black"} href={"#"}>
                {"体验语音生成"}
              </a>
            </div>
          </div>
        </section>
        <section className={"identity wrap reveal"} id={"identity"}>
          <h2 className={"section-title"}>
            {"打造值得被记住的声音身份"}
          </h2>
          <p className={"subtle"}>
            {"设计生成 · 专业音色管理"}
          </p>
          <div className={"voice-grid"}>
            <article className={"voice-card"}>
              <div className={"voice-orb"}>
                {"⌁"}
              </div>
              <b>
                {"Warm Narrator"}
              </b>
              <div className={"tags"}>
                <span>
                  {"沉稳"}
                </span>
                <span>
                  {"中文"}
                </span>
                <span>
                  {"叙述"}
                </span>
              </div>
            </article>
            <article className={"voice-card"}>
              <div className={"voice-orb"}>
                {"⌁"}
              </div>
              <b>
                {"Bright Companion"}
              </b>
              <div className={"tags"}>
                <span>
                  {"陪伴"}
                </span>
                <span>
                  {"英语"}
                </span>
                <span>
                  {"对话"}
                </span>
              </div>
            </article>
            <article className={"voice-card"}>
              <div className={"voice-orb"}>
                {"⌁"}
              </div>
              <b>
                {"Calm Mentor"}
              </b>
              <div className={"tags"}>
                <span>
                  {"温暖"}
                </span>
                <span>
                  {"法语"}
                </span>
                <span>
                  {"旁白"}
                </span>
              </div>
            </article>
            <article className={"voice-card create"}>
              <strong>
                {"＋"}
              </strong>
              <span>
                {"创建新音色"}
                <br />
                {"上传或录制"}
              </span>
            </article>
          </div>
        </section>
        <section className={"scenarios wrap reveal"}>
          <h2 className={"section-title"}>
            {"为不同产品场景而设计"}
          </h2>
          <section className={"solution-group"} aria-label={"Lychee Agent 方案"}>
            <div className={"solution-group-heading"}>
              <span>
                {"LYCHEE AGENT"}
              </span>
              <span className={"solution-group-status"}>
                {"· 即将上线"}
              </span>
            </div>
            <div className={"scenario-grid"}>
              <article className={"scenario-card solution-agent"}>
                <div className={"illustration"}>
                  <svg className={"scenario-icon"} viewBox={"0 0 56 56"} aria-hidden={"true"}>
                    <path d={"M20 10v-5m16 5v-5"}></path>
                    <rect x={"10"} y={"12"} width={"36"} height={"30"} rx={"9"}></rect>
                    <circle cx={"22"} cy={"27"} r={"2"} fill={"#151515"}></circle>
                    <circle cx={"34"} cy={"27"} r={"2"} fill={"#151515"}></circle>
                    <path d={"M20 35c5 3 11 3 16 0M28 42v6m-8 3h16"}></path>
                  </svg>
                </div>
                <div>
                  <h3>
                    {"AI 玩具与陪伴硬件"}
                  </h3>
                  <p>
                    {"为玩具、机器人、陪伴设备提供自然、低延迟的语音交互体验。"}
                  </p>
                </div>
              </article>
              <article className={"scenario-card solution-agent"}>
                <div className={"illustration"}>
                  <svg className={"scenario-icon"} viewBox={"0 0 56 56"} aria-hidden={"true"}>
                    <rect x={"9"} y={"13"} width={"31"} height={"23"} rx={"6"}></rect>
                    <path d={"m18 36-5 7v-7M22 23h9m-9 6h6"}></path>
                    <path d={"M42 22h5v18H31"}></path>
                  </svg>
                </div>
                <div>
                  <h3>
                    {"对话式 AI 助手"}
                  </h3>
                  <p>
                    {"为智能助手提供自然连贯的多轮对话与语音交互体验。"}
                  </p>
                </div>
              </article>
            </div>
          </section>
          <section className={"solution-group"} aria-label={"Lychee Hub 方案"}>
            <div className={"solution-group-heading"}>
              <span>
                {"LYCHEE HUB"}
              </span>
            </div>
            <div className={"scenario-grid"}>
              <a className={"scenario-card solution-hub"} href={"/hub/ai-voice"} aria-label={"查看视频本地化配音的 Lychee Hub 方案"}>
                <div className={"illustration"}>
                  <svg className={"scenario-icon"} viewBox={"0 0 56 56"} aria-hidden={"true"}>
                    <rect x={"8"} y={"14"} width={"40"} height={"28"} rx={"4"}></rect>
                    <path d={"m25 22 10 6-10 6V22Z"}></path>
                    <path d={"M15 48h26M20 42v6m16-6v6"}></path>
                  </svg>
                </div>
                <div>
                  <h3>
                    {"视频本地化配音"}
                  </h3>
                  <p>
                    {"为影视、短视频与课程提供多语种配音，提升本地化效率与观看体验。"}
                  </p>
                </div>
                <span className={"card-arrow"} aria-hidden={"true"}>
                  {"→"}
                </span>
              </a>
              <a className={"scenario-card solution-hub"} href={"/hub/ai-voice"} aria-label={"查看有声内容产品的 Lychee Hub 方案"}>
                <div className={"illustration"}>
                  <svg className={"scenario-icon"} viewBox={"0 0 56 56"} aria-hidden={"true"}>
                    <path d={"M28 15c-6-5-13-4-18 0v25c5-4 12-5 18 0 6-5 13-4 18 0V15c-5-4-12-5-18 0Z"}></path>
                    <path d={"M28 15v25M16 22h7m10 0h7M16 29h7m10 0h7"}></path>
                  </svg>
                </div>
                <div>
                  <h3>
                    {"有声内容产品"}
                  </h3>
                  <p>
                    {"为有声书、播客和知识内容生成自然、富有表现力的声音。"}
                  </p>
                </div>
                <span className={"card-arrow"} aria-hidden={"true"}>
                  {"→"}
                </span>
              </a>
            </div>
          </section>
        </section>
 
        <section className={"conversion wrap reveal"} id={"start"}>
          <div className={"conversion-inner"}>
            <div className={"conversion-copy"}>
              <h2>
                {"准备好接入 LycheeVoice？"}
              </h2>
              <p>
                {"适合开发者快速试用与团队评估接入。"}
              </p>
            </div>
            <div className={"conversion-actions"}>
              <a className={"btn black"} href={"/hub/premium-translation"}>
                {"立即试用　→"}
              </a>
              <a className={"btn"} href={"#"}>
                {"联系解决方案专家　→"}
              </a>
            </div>
          </div>
        </section> 
      
      </main>
      <SiteFooter />
      <div className={"agent-toast"}>
        {"Lychee Agent 的 AI 玩具介绍方案即将上线"}
      </div>
    </div>
  );
};
